import { Button, CircularProgress } from '@mui/material';
import { useEffect } from 'react';
import useNdaFiles from '../items/hooks/HNDA';

interface NdaFile {
    name: string;
    fullPath: string;
    url: string;
    size: number;
    createdOn: string | null;
    lastModified: string | null;
}

interface GNdaFilesTableProps {
    userId?: string | number | null;
}

export default function GNdaFilesTable({ userId = null }: GNdaFilesTableProps) {
    const { files, loading, error, reload } = useNdaFiles();

    useEffect(() => {
        // If a userId is provided, reload files including the userId so backend can include permissions
        void reload(userId ?? undefined);
    }, [userId, reload]);

    const formatDate = (dateString: string | null): string => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        } catch {
            return dateString;
        }
    };

    const formatSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const handleDownload = (file: NdaFile) => {
        window.open(file.url, '_blank');
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: 20, color: 'red' }}>
                <p>Error: {error}</p>
                <Button variant='contained' color='primary' onClick={() => void reload(userId ?? undefined)}>
                    Retry
                </Button>
            </div>
        );
    }

    if (files.length === 0) {
        return (
            <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
                <p>No NDA files found</p>
            </div>
        );
    }

    return (
        <div className='standard-div'>
            <table className='elegant-table users-rounded-table no-borders'>
                <thead>
                    <tr>
                        <th>Filename</th>
                        <th>Size</th>
                        <th>Created Date</th>
                        <th>Last Modified</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {files.map((file, index) => (
                        <tr key={index}>
                            <td>{file.name}</td>
                            <td>{formatSize(file.size)}</td>
                            <td>{formatDate(file.createdOn)}</td>
                            <td>{formatDate(file.lastModified)}</td>
                            <td>
                                <Button
                                    variant='outlined'
                                    size='small'
                                    color='primary'
                                    onClick={() => handleDownload(file)}
                                >
                                    Download
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
